import React from 'react';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { PARTNER_ENTRANCE } from '../../../../util/partnerEntrance';

import { NamedLink } from '../../../../components';
import { getCategoryIcon } from '../../../../components/CategoryMenu/icons';
import {
  categorySearchString,
  visibleCategories,
} from '../../../../components/CategoryMenu/helpers';

import { categoryImage } from './categoryImages';
import css from './SectionCategoryTiles.module.css';

/**
 * A circle per visible top-level category, high on the homepage so a visitor
 * can go straight to what they came for.
 *
 * No visible heading, at the client's request — the photographs say what the
 * row is, and a title above them only pushed the products further down. The
 * section keeps an aria-label so the region is still announced.
 *
 * Each circle carries a product photo supplied by the client
 * (see categoryImages.js); a category without one yet falls back to the icon
 * the topbar menu uses, so the row is never broken while artwork is pending.
 *
 * Reads the same merged category tree as the topbar's CategoryMenu (hosted
 * asset over configCategories.js), drops `hidden: true` entries with the same
 * helper, and links with the same search string. Second-level categories are
 * deliberately not shown here.
 *
 * @returns {JSX.Element|null}
 */
const SectionCategoryTiles = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const categories = visibleCategories(config.categoryConfiguration?.categories);

  if (categories.length === 0) {
    return null;
  }

  return (
    <section
      className={css.root}
      aria-label={intl.formatMessage({ id: 'SectionCategoryTiles.title' })}
    >
      <div className={css.inner}>
        <ul className={css.tiles}>
          {categories.map(category => {
            const photo = categoryImage(category.id);
            const Icon = getCategoryIcon(category.iconName);
            return (
              <li key={category.id} className={css.tileItem}>
                <NamedLink
                  className={css.tile}
                  name="SearchPage"
                  to={{ search: categorySearchString([category.id]) }}
                >
                  <span className={css.circle}>
                    {photo ? (
                      <img className={css.photo} src={photo} alt="" loading="lazy" />
                    ) : Icon ? (
                      <Icon className={css.icon} />
                    ) : null}
                  </span>
                  <span className={css.name}>{category.name}</span>
                </NamedLink>
              </li>
            );
          })}
          {/* The fourth circle: the way into Zuprize Partner. */}
          <li className={css.tileItem}>
            <NamedLink className={css.tile} name={PARTNER_ENTRANCE.routeName}>
              <span className={css.circle}>
                <img
                  className={css.photo}
                  src={PARTNER_ENTRANCE.badgeImage}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className={css.name}>
                <FormattedMessage id={PARTNER_ENTRANCE.labelId} />
              </span>
            </NamedLink>
          </li>
        </ul>
      </div>
    </section>
  );
};

export default SectionCategoryTiles;
